import React, { useEffect, useMemo, useRef, useState } from 'react';
import './web-form.sass';
import Loader from 'react-spinners/BounceLoader';
import { useLocation } from 'react-router-dom';
import { getNestedObjectSafely } from '../utils/functions';
import { fetchSurveyJson } from './web-form.service';
import SurveySubmitted from './SurveySubmitted';
import NpsQuestion from './npsQuestion';
import RangeQuestion from './RangeQuestion';
import OpenEnded from './openEnded';
import OpenEndedDate from './openEndedDate';
import CESQuestion from './cesQuestion';
import YearQuestion from './yearQuestion';
import SingleSelect from './singleSelect';
import MultipleSelect from './multipleSelect';
import FallBack from './fallback';
import CSAT from './CSAT';
import ResolutionScore from './ResolutionScore';
import IntegerChoice from './IntegerChoice';

const DEFAULT_COMPLETE_MESSAGE = 'Thankyou for your participation!';

// Question types that should appear on their own page
const STANDALONE_QUESTION_TYPES = [
  'END_MESSAGE',
  'MESSAGE_END',
  'OPEN_ENDED_NPS_0_10',
  'NPS_CHOICE',
  'INTRO_MESSAGE_MULTIPLE_CHOICE',
];

function shouldBeStandalone(questionType) {
  return STANDALONE_QUESTION_TYPES.includes(questionType);
}

function safeParseJson(maybeJson) {
  if (maybeJson === null || maybeJson === undefined) return null;
  if (Array.isArray(maybeJson) || typeof maybeJson === 'object') return maybeJson;
  if (typeof maybeJson !== 'string') return null;
  try {
    return JSON.parse(maybeJson);
  } catch {
    return null;
  }
}

function traverseQuestionsDepthFirst(questions, out = []) {
  if (!Array.isArray(questions)) return out;
  for (const q of questions) {
    out.push(q);
    const answers = Array.isArray(q?.answers) ? q.answers : [];
    for (const a of answers) {
      if (Array.isArray(a?.questions) && a.questions.length > 0) {
        traverseQuestionsDepthFirst(a.questions, out);
      }
    }
  }
  return out;
}

function normalizeQuestionType(typeObjOrString) {
  if (!typeObjOrString) return '';
  if (typeof typeObjOrString === 'string') return typeObjOrString;
  return typeObjOrString?.key || '';
}

function toWebFormQuestion(q) {
  const questionType = normalizeQuestionType(q?.type);
  const answerList = Array.isArray(q?.answers) ? q.answers.map((a) => a?.text ?? '') : [];
  return {
    questionID: q?.id ?? '',
    questionType,
    questionText: q?.text ?? '',
    questionLevel: q?.level ?? '',
    required: false,
    lastQuestion: false,
    answerList,
    maxLength: q?.maxLength,
    inputType: q?.inputType,
    validationRule: q?.validationRule,
    progress: 0,
  };
}

function getAnswerByIndex(questionRaw, oneBasedIndexString) {
  const idx = Number(oneBasedIndexString) - 1;
  const answers = Array.isArray(questionRaw?.answers) ? questionRaw.answers : [];
  if (!Number.isFinite(idx) || idx < 0 || idx >= answers.length) return null;
  return answers[idx];
}

function evaluateRule(rule, answerValue) {
  if (!rule || answerValue === '' || answerValue === null || answerValue === undefined)
    return false;
  const numValue = Number(answerValue);
  if (!Number.isFinite(numValue)) return false;
  const ruleType = rule.ruletype || rule.ruleType;
  const ruleValue = rule.value;
  if (ruleType === 'btw') {
    const parts = String(ruleValue).split(',');
    if (parts.length === 2) {
      const min = Number(parts[0]);
      const max = Number(parts[1]);
      return numValue >= min && numValue <= max;
    }
    const dashParts = String(ruleValue).split('-');
    if (dashParts.length === 2) {
      const min = Number(dashParts[0]);
      const max = Number(dashParts[1]);
      return numValue >= min && numValue <= max;
    }
  }
  return false;
}

function buildQuestionPath(questions, answersById, path = []) {
  if (!Array.isArray(questions)) return path;
  for (const question of questions) {
    const qType = normalizeQuestionType(question?.type);
    const isNestedEndMessage = qType === 'END_MESSAGE' && path.length > 0;
    if (!isNestedEndMessage) {
      path.push(question);
    }
    const qId = String(question?.id ?? '');
    const answer = answersById?.[qId]?.responseText;
    const answers = Array.isArray(question?.answers) ? question.answers : [];
    if (answers.length > 0) {
      const hasAnswer =
        answer?.value !== '' &&
        answer?.value !== null &&
        answer?.value !== undefined &&
        (!Array.isArray(answer.value) || answer.value.length > 0);
      if (hasAnswer) {
        let foundBranch = false;
        if (qType !== 'MULTIPLE_CHOICE_MULTIPLE_SELECT') {
          const selectedIndex = answer.value;
          const selectedAnswer = getAnswerByIndex(question, selectedIndex);
          if (selectedAnswer) {
            const nestedQuestions = Array.isArray(selectedAnswer?.questions)
              ? selectedAnswer.questions
              : [];
            if (nestedQuestions.length > 0) {
              const firstNested = nestedQuestions[0];
              const nestedType = normalizeQuestionType(firstNested?.type);
              if (nestedType === 'END_MESSAGE' || nestedType === 'MESSAGE_END') {
                path.push(firstNested);
                return path;
              }
              buildQuestionPath(nestedQuestions, answersById, path);
              foundBranch = true;
            }
          }
        }
        if (!foundBranch) {
          for (const ans of answers) {
            const ansType = normalizeQuestionType(ans?.type);
            if (ansType === 'LOGICAL_BRANCH') {
              const rules = Array.isArray(ans?.rules) ? ans.rules : [];
              for (const rule of rules) {
                if (evaluateRule(rule, answer.value)) {
                  const nestedQuestions = Array.isArray(ans?.questions) ? ans.questions : [];
                  if (nestedQuestions.length > 0) {
                    buildQuestionPath(nestedQuestions, answersById, path);
                    foundBranch = true;
                    break;
                  }
                }
              }
              if (foundBranch) break;
            }
          }
        }
      }
    }
  }
  return path;
}

// Group questions into pages based on standalone rules
function groupQuestionsIntoPages(questionPath) {
  const pages = [];
  let currentPage = [];

  for (const question of questionPath) {
    const qType = normalizeQuestionType(question?.type);
    const isStandalone = shouldBeStandalone(qType);

    if (isStandalone) {
      // If current page has questions, push it first
      if (currentPage.length > 0) {
        pages.push(currentPage);
        currentPage = [];
      }
      // Add standalone question as its own page
      pages.push([question]);
    } else {
      // Add to current page
      currentPage.push(question);
    }
  }

  // Push any remaining questions
  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
}

export default function WebformPreview() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [surveyCompleteMessage, setSurveyCompleteMessage] = useState(DEFAULT_COMPLETE_MESSAGE);
  const [accountId] = useState(null);
  const [accountLogo, setAccountLogo] = useState(null);
  const [surveyTitle, setSurveyTitle] = useState('Survey Preview');
  const [surveyTree, setSurveyTree] = useState([]);
  const allQuestionsFlat = useMemo(() => traverseQuestionsDepthFirst(surveyTree, []), [surveyTree]);
  const [answersById, setAnswersById] = useState({});

  const questionPath = useMemo(() => {
    return buildQuestionPath(surveyTree, answersById, []);
  }, [surveyTree, answersById]);

  // Group questions into pages
  const pages = useMemo(() => {
    return groupQuestionsIntoPages(questionPath);
  }, [questionPath]);

  const answersByIdRef = useRef(answersById);
  useEffect(() => {
    answersByIdRef.current = answersById;
  }, [answersById]);

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [historyStack, setHistoryStack] = useState([]);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const surveyIdParam = params.get('surveyId') || params.get('survedyId');

  useEffect(() => {
    async function loadPreview() {
      setLoading(true);
      setErrorMessage('');
      setSurveyCompleted(false);
      try {
        if (!surveyIdParam) {
          throw new Error('Missing surveyId (or survedyId) in URL');
        }
        const resp = await fetchSurveyJson(surveyIdParam);
        const payload = resp?.data;
        const dataStrOrObj =
          payload?.data ??
          getNestedObjectSafely(payload, 'Data') ??
          getNestedObjectSafely(payload, 'Data', 'data') ??
          getNestedObjectSafely(payload, 'Data', 'Data');
        const parsed = safeParseJson(dataStrOrObj);
        if (!Array.isArray(parsed)) {
          throw new Error('Survey JSON response was not an array of questions');
        }
        setSurveyTree(parsed);
        setSurveyTitle(`Survey Preview (${surveyIdParam})`);

        const initialAnswers = {};
        for (const q of traverseQuestionsDepthFirst(parsed, [])) {
          const qType = normalizeQuestionType(q?.type);
          const isMulti = qType === 'MULTIPLE_CHOICE_MULTIPLE_SELECT';
          initialAnswers[q?.id ?? ''] = {
            responseText: isMulti ? { value: [], text: [] } : { value: '', text: '' },
            questionID: q?.id ?? '',
            questionLevel: q?.level ?? '',
            questionType: qType,
            required: false,
            isFinal: false,
          };
        }
        setAnswersById(initialAnswers);
        setCurrentPageIndex(0);
        setHistoryStack([]);
      } catch (e) {
        setErrorMessage(e?.message || 'Failed to load survey preview');
      } finally {
        setLoading(false);
      }
    }
    loadPreview();
  }, [surveyIdParam]);

  const currentPage = useMemo(() => {
    return pages[currentPageIndex] || [];
  }, [pages, currentPageIndex]);

  const onQuestionValueChanged = (responseText, questionID) => {
    setAnswersById((prev) => ({
      ...prev,
      [questionID]: {
        ...(prev?.[questionID] || {}),
        responseText,
      },
    }));
  };

  const goNext = () => {
    // Check if any question on current page is END_MESSAGE
    const hasEndMessage = currentPage.some((q) => {
      const qType = normalizeQuestionType(q?.type);
      return qType === 'END_MESSAGE' || qType === 'MESSAGE_END';
    });

    if (hasEndMessage) {
      const endMsg = currentPage.find((q) => {
        const qType = normalizeQuestionType(q?.type);
        return qType === 'END_MESSAGE' || qType === 'MESSAGE_END';
      });
      const messageText = endMsg?.text || DEFAULT_COMPLETE_MESSAGE;
      setSurveyCompleteMessage(messageText);
      setSurveyCompleted(true);
      return;
    }

    if (currentPageIndex >= pages.length - 1) {
      setSurveyCompleted(true);
      setSurveyCompleteMessage(DEFAULT_COMPLETE_MESSAGE);
      return;
    }

    setHistoryStack((h) => [...h, currentPageIndex]);
    setCurrentPageIndex(currentPageIndex + 1);
    window.scrollTo(0, 0);
  };

  const goPrev = () => {
    setHistoryStack((h) => {
      if (h.length === 0) return h;
      const newH = [...h];
      const prevPageIndex = newH.pop();
      setCurrentPageIndex(prevPageIndex);
      window.scrollTo(0, 0);
      return newH;
    });
  };

  const progress = useMemo(() => {
    if (!questionPath.length) return 0;
    let answeredCount = 0;
    for (const q of questionPath) {
      const qId = String(q?.id);
      const response = answersById?.[qId]?.responseText?.value;
      const hasAnswer =
        response !== '' &&
        response !== null &&
        response !== undefined &&
        (!Array.isArray(response) || response.length > 0);
      if (hasAnswer) answeredCount++;
    }
    return Math.round((answeredCount / questionPath.length) * 100);
  }, [questionPath, answersById]);

  const user = '';
  const isTappi = accountId === '73450';
  const isNutriGo = accountId === '132990';

  const renderQuestion = (questionRaw) => {
    const q = toWebFormQuestion(questionRaw);
    let questionText = q.questionText || '';
    if (!questionText.includes('<strong>')) {
      questionText = `<strong>${questionText}</strong>`;
    }
    const enhancedQ = { ...q, questionText };

    const qId = q.questionID;
    const currentValue = answersById?.[qId]?.responseText?.value ?? '';
    const currentResponseText = answersById?.[qId]?.responseText ?? { value: '', text: '' };

    if (q.questionType === 'OPEN_ENDED_NPS_0_10' || q.questionType === 'NPS_CHOICE') {
      return (
        <NpsQuestion
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
          user={user}
        />
      );
    } else if (q.questionType === 'OPEN_ENDED_NPS' || q.questionType === 'OPEN_ENDED') {
      return (
        <OpenEnded
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
          user={user}
        />
      );
    } else if (q.questionType === 'OPEN_ENDED_RANGE_0_10' || q.questionType === 'RANGE_0_10') {
      return (
        <RangeQuestion
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
        />
      );
    } else if (
      q.questionType === 'MULTIPLE_CHOICE_SINGLE_SELECT' ||
      q.questionType === 'INTRO_MESSAGE_MULTIPLE_CHOICE'
    ) {
      return (
        <SingleSelect
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
          user={user}
        />
      );
    } else if (q.questionType === 'CUSTOMER_SATISFACTION_SCORE') {
      return (
        <CSAT
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
        />
      );
    } else if (q.questionType === 'OPEN_ENDED_INTEGER') {
      return (
        <IntegerChoice
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
        />
      );
    } else if (q.questionType === 'RESOLUTION_SCORE') {
      return (
        <ResolutionScore
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
        />
      );
    } else if (q.questionType === 'MULTIPLE_CHOICE_MULTIPLE_SELECT') {
      return (
        <MultipleSelect
          key={qId}
          question={enhancedQ}
          questionValue={currentResponseText}
          onChange={onQuestionValueChanged}
        />
      );
    } else if (q.questionType === 'CUSTOMER_EFFORT_SCORE') {
      return (
        <CESQuestion
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
        />
      );
    } else if (q.questionType === 'OPEN_ENDED_DATE') {
      return (
        <OpenEndedDate
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
        />
      );
    } else if (q.questionType === 'OPEN_ENDED_YEAR_YYYY') {
      return (
        <YearQuestion
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
        />
      );
    } else if (
      q.questionType === 'MESSAGE_END' ||
      q.questionType === 'ERROR_TYPE' ||
      q.questionType === 'END_MESSAGE' ||
      q.questionType === 'INTRO_MESSAGE_MESSAGE' ||
      q.questionType === 'MESSAGE' ||
      q.questionType === 'QuestionError'
    ) {
      return <FallBack key={qId} question={enhancedQ} />;
    } else {
      return (
        <OpenEnded
          key={qId}
          question={enhancedQ}
          value={currentValue}
          onChange={onQuestionValueChanged}
          user={user}
        />
      );
    }
  };

  return (
    <section className="web-form-container web-form-container-font">
      <div>
        <div className="logo-section">
          {accountLogo ? (
            <img
              onError={() => setAccountLogo(null)}
              className="company-logo"
              alt="company logo"
              src={accountLogo}
            />
          ) : (
            <div className="company-logo">&nbsp;</div>
          )}
        </div>
        {surveyCompleted && <SurveySubmitted message={surveyCompleteMessage} />}
        {!surveyCompleted && (
          <section className="web-view">
            {loading && (
              <div style={{ zIndex: '999999', background: 'white' }}>
                <div className="loader-container">
                  <Loader size={50} color="#2196f3" loading={loading} />
                </div>
              </div>
            )}
            {!loading && (
              <div className="web-container content-container">
                <h2 className="survey-title">
                  <strong>{surveyTitle}</strong>
                </h2>
                <div id="webFormProgress">
                  <div
                    id={isTappi ? 'tappi-webform-bar' : 'webFormBar'}
                    style={{ width: `${progress || 0}%` }}
                  >
                    {progress || 0}%
                  </div>
                </div>
                {errorMessage && <div className="footer">{errorMessage}</div>}
                {currentPage.length > 0 && (
                  <>
                    {currentPage.map((questionRaw) => renderQuestion(questionRaw))}
                    <div className="d-flex gap-3 justify-content-between">
                      <button
                        className={
                          isNutriGo || isTappi ? 'btn btn-primary tappi-btn' : 'submit-button'
                        }
                        onClick={goPrev}
                        type="button"
                        disabled={historyStack.length === 0}
                        style={{ opacity: historyStack.length === 0 ? 0.6 : 1 }}
                      >
                        Previous
                      </button>
                      <button
                        className={
                          isNutriGo || isTappi ? 'btn btn-primary tappi-btn' : 'submit-button'
                        }
                        onClick={goNext}
                        type="button"
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </section>
  );
}
